import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isChatRoute = location.pathname.startsWith("/chat/") ||
    (!/^\/(login|register|dashboard|onboarding|subscription|feed|profile|notifications|)$/.test(location.pathname) &&
     !location.pathname.startsWith("/assistant/") &&
     !location.pathname.startsWith("/profile/") &&
     /^\/[a-z0-9-]+$/.test(location.pathname));

  // Hide navbar on social pages (they have their own nav)
  const isSocialPage = location.pathname === "/feed" ||
    location.pathname.startsWith("/profile/") ||
    location.pathname === "/notifications" ||
    location.pathname === "/explore" ||
    location.pathname.startsWith("/settings/") ||
    location.pathname === "/relationships" ||
    location.pathname === "/achievements" ||
    location.pathname === "/leaderboard" ||
    location.pathname.startsWith("/marketplace") ||
    location.pathname.startsWith("/creators") ||
    location.pathname === "/integrations" ||
    location.pathname === "/publish" ||
    location.pathname === "/analytics" ||
    location.pathname === "/settings/social-accounts";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isChatRoute || isSocialPage) return null;

  const handleLogout = () => { logout(); navigate("/"); };
  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`sticky top-0 z-50 nav-glass transition-all duration-200 ${scrolled ? "shadow-[0_8px_30px_rgba(2,6,23,0.6)]" : ""}`} data-testid="main-nav">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline group" data-testid="nav-logo">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center group-hover:scale-105 transition-transform shadow-glow-sm">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-outfit text-lg font-semibold text-gradient">App-Mi.ai</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {user ? (
            <>
              <NavLink to="/feed" active={isActive("/feed")} testId="nav-feed">Feed</NavLink>
              <NavLink to="/dashboard" active={isActive("/dashboard")} testId="nav-dashboard">My Companions</NavLink>
              <NavLink to="/onboarding" active={isActive("/onboarding")} testId="nav-create-ai">Create AI</NavLink>
              <NavLink to="/subscription" active={isActive("/subscription")} testId="nav-subscription">Subscription</NavLink>
              {user.role === "admin" && <NavLink to="/admin/moderation" active={isActive("/admin/moderation")} testId="nav-moderation">Moderation</NavLink>}
              <div className="w-px h-6 bg-muted mx-2" />
              <button onClick={handleLogout} className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-red-400 hover:bg-red-500/10 bg-transparent border-0 cursor-pointer transition-colors press-down" data-testid="nav-logout-btn">Logout</button>
            </>
          ) : (
            <>
              <a href="/#assistants" className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 no-underline transition-colors">Companions</a>
              <a href="/#pricing" className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 no-underline transition-colors">Pricing</a>
              <div className="w-px h-6 bg-muted mx-2" />
              <Link to="/login" className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 no-underline transition-colors" data-testid="nav-login-link">Login</Link>
              <Link to="/register" className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-cyan-400 to-violet-500 text-white no-underline hover:-translate-y-0.5 hover:scale-[1.02] transition-all shadow-glow-sm hover:shadow-glow press-down" data-testid="nav-register-link">Get Started</Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden bg-transparent border-0 cursor-pointer text-foreground/80" data-testid="nav-mobile-toggle">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-card border-t border-border/60 px-6 py-4 space-y-1 animate-fade-in" data-testid="nav-mobile-menu">
          {user ? (
            <>
              <Link to="/feed" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:bg-white/5 no-underline" onClick={() => setMobileOpen(false)}>Feed</Link>
              <Link to="/dashboard" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:bg-white/5 no-underline" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <Link to="/onboarding" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:bg-white/5 no-underline" onClick={() => setMobileOpen(false)}>Create AI</Link>
              <Link to="/subscription" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:bg-white/5 no-underline" onClick={() => setMobileOpen(false)}>Subscription</Link>
              {user.role === "admin" && <Link to="/admin/moderation" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:bg-white/5 no-underline" onClick={() => setMobileOpen(false)}>Moderation</Link>}
              <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 bg-transparent border-0 cursor-pointer">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block px-3 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:bg-white/5 no-underline" onClick={() => setMobileOpen(false)}>Login</Link>
              <Link to="/register" className="block px-3 py-2 rounded-lg text-sm font-medium text-cyan-400 hover:bg-cyan-500/10 no-underline" onClick={() => setMobileOpen(false)}>Get Started</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

function NavLink({ to, active, children, testId }) {
  return (
    <Link
      to={to}
      className={`relative px-3 py-2 rounded-lg text-sm font-medium no-underline transition-colors ${active ? "text-cyan-400 bg-cyan-500/10" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}
      data-testid={testId}
    >
      {children}
      {active && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-gradient-to-r from-cyan-400 to-violet-500 rounded-full" />}
    </Link>
  );
}
